import React, { useState, useMemo } from "react";
import {
  Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditSharpIcon from "@mui/icons-material/EditSharp";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";

import {
  useGetRolesQuery,
  useGetPrivilegesQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
} from "../../../Api/attoDeskApi";
import { IModulePermission, IPrivilege, IRole } from "../../../Api/Interface/api.interface";
import { useNotifier } from "../../../Core/Notifier";

// Permission actions config
const ACTIONS: { key: keyof IModulePermission; label: string; color: string }[] = [
  { key: "can_view",   label: "View",   color: "#0ea5e9" },
  { key: "can_create", label: "Create", color: "#16a34a" },
  { key: "can_edit",   label: "Edit",   color: "#f59e0b" },
  { key: "can_delete", label: "Delete", color: "#ef4444" },
];

const PERM_CHIP_COLORS: Record<string, string> = {
  v: "#dbeafe", c: "#dcfce7", e: "#fef3c7", d: "#fee2e2",
};

const makeEmptyPerms = (modules: IPrivilege[]): Record<string, IModulePermission> => {
  const out: Record<string, IModulePermission> = {};
  modules.forEach((m) => {
    out[m.key] = { can_view: false, can_create: false, can_edit: false, can_delete: false };
  });
  return out;
};

const UserRolesPage: React.FC = () => {
  const { showMessage, showErrorMessage } = useNotifier();

  const { data: rolesData, isFetching: rolesLoading, refetch } = useGetRolesQuery();
  const { data: privilegesData } = useGetPrivilegesQuery();
  const [createRole, { isLoading: creating }] = useCreateRoleMutation();
  const [updateRole, { isLoading: updating }] = useUpdateRoleMutation();
  const [deleteRole, { isLoading: deleting }] = useDeleteRoleMutation();

  const roles: IRole[]     = useMemo(() => (rolesData?.data as IRole[]) ?? [], [rolesData]);
  const modules: IPrivilege[] = useMemo(() => (privilegesData?.data as IPrivilege[]) ?? [], [privilegesData]);

  // ── Dialog state ────────────────────────────────────────────────────────────
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<IRole | null>(null);
  const [formName, setFormName]         = useState("");
  const [formDesc, setFormDesc]         = useState("");
  const [formPerms, setFormPerms] = useState<Record<string, IModulePermission>>({});

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<IRole | null>(null);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const openCreate = () => {
    setEditTarget(null);
    setFormName(""); setFormDesc("");
    setFormPerms(makeEmptyPerms(modules));
    setDialogOpen(true);
  };

  const openEdit = (role: IRole) => {
    setEditTarget(role);
    setFormName(role.name);
    setFormDesc(role.description ?? "");
    // Build full permission map (fill in any missing module with false)
    const perms = makeEmptyPerms(modules);
    Object.entries(role.permissions).forEach(([k, p]) => { if (perms[k]) perms[k] = { ...p }; });
    setFormPerms(perms);
    setDialogOpen(true);
  };

  const togglePerm = (moduleKey: string, action: keyof IModulePermission) => {
    setFormPerms((prev) => ({
      ...prev,
      [moduleKey]: { ...prev[moduleKey], [action]: !prev[moduleKey]?.[action] },
    }));
  };

  const toggleAllForModule = (moduleKey: string) => {
    const current = formPerms[moduleKey];
    const allOn = ACTIONS.every((a) => current?.[a.key]);
    setFormPerms((prev) => ({
      ...prev,
      [moduleKey]: { can_view: !allOn, can_create: !allOn, can_edit: !allOn, can_delete: !allOn },
    }));
  };

  const toggleAllForAction = (action: keyof IModulePermission) => {
    const allOn = modules.every((m) => formPerms[m.key]?.[action]);
    setFormPerms((prev) => {
      const next = { ...prev };
      modules.forEach((m) => { next[m.key] = { ...next[m.key], [action]: !allOn }; });
      return next;
    });
  };

  const handleSave = async () => {
    if (!formName.trim()) { showErrorMessage("Role name is required"); return; }
    try {
      const payload = { name: formName.trim(), description: formDesc.trim(), permissions: formPerms };
      if (editTarget) {
        await updateRole({ id: editTarget.id, ...payload }).unwrap();
        showMessage("Role updated", "success");
      } else {
        await createRole(payload).unwrap();
        showMessage("Role created", "success");
      }
      setDialogOpen(false);
      refetch();
    } catch {
      showErrorMessage("Failed to save role");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteRole(deleteTarget.id).unwrap();
      showMessage("Role deleted", "success");
      setDeleteDialogOpen(false);
      refetch();
    } catch {
      showErrorMessage("Failed to delete role");
    }
  };

  // ── Permission summary chips for role card ──────────────────────────────────
  const permSummary = (role: IRole) => {
    const granted: string[] = [];
    Object.entries(role.permissions).forEach(([k, p]) => {
      if (p.can_view || p.can_create || p.can_edit || p.can_delete) granted.push(k);
    });
    return granted.slice(0, 6);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ borderRadius: 3, bgcolor: "#2f3891", p: 3, mb: 3, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <VerifiedUserOutlinedIcon sx={{ color: "#fff", fontSize: 24 }} />
          </Box>
          <Box>
            <Typography fontWeight={700} fontSize={18} color="#fff">User Roles</Typography>
            <Typography fontSize={12} color="rgba(255,255,255,0.7)">Define roles and assign module permissions</Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}
          sx={{ textTransform: "none", borderRadius: 2, px: 2.5, bgcolor: "#fff", color: "#2f3891",
            fontWeight: 700, "&:hover": { bgcolor: "#f1f5f9" } }}>
          Add Role
        </Button>
      </Box>

      {/* Roles list */}
      {rolesLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress sx={{ color: "#2f3891" }} />
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {roles.map((role) => (
            <Paper key={role.id} elevation={0}
              sx={{ border: "1px solid #e2e8f0", borderRadius: 3, p: 2.5, "&:hover": { boxShadow: "0 2px 12px rgba(0,0,0,0.08)" } }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  {/* Role name + stats */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
                    <ShieldOutlinedIcon sx={{ fontSize: 18, color: "#2f3891" }} />
                    <Typography fontWeight={700} fontSize={15} color="#0f172a">{role.name}</Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: 0.5 }}>
                      <PeopleOutlinedIcon sx={{ fontSize: 13, color: "#94a3b8" }} />
                      <Typography fontSize={11} color="#94a3b8">{role.user_count} user{role.user_count !== 1 ? "s" : ""}</Typography>
                    </Box>
                    <Chip label={`${Object.values(role.permissions).filter((p) => p.can_view || p.can_create || p.can_edit || p.can_delete).length} permissions`}
                      size="small" sx={{ bgcolor: "#eef2ff", color: "#2f3891", fontWeight: 600, fontSize: 10 }} />
                  </Box>

                  {role.description && (
                    <Typography fontSize={12} color="#64748b" mb={1}>{role.description}</Typography>
                  )}

                  {/* Permission summary chips */}
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {permSummary(role).map((k) => {
                      const mod = modules.find((m) => m.key === k);
                      const p = role.permissions[k];
                      const tags = [
                        p.can_view   && { label: "V", bg: PERM_CHIP_COLORS.v, color: "#1d4ed8" },
                        p.can_create && { label: "C", bg: PERM_CHIP_COLORS.c, color: "#166534" },
                        p.can_edit   && { label: "E", bg: PERM_CHIP_COLORS.e, color: "#92400e" },
                        p.can_delete && { label: "D", bg: PERM_CHIP_COLORS.d, color: "#991b1b" },
                      ].filter(Boolean) as { label: string; bg: string; color: string }[];
                      return (
                        <Box key={k} sx={{ display: "flex", alignItems: "center", gap: 0.25, bgcolor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 1.5, px: 0.75, py: 0.25 }}>
                          <Typography fontSize={11} color="#475569" fontWeight={500}>{mod?.display ?? k}</Typography>
                          {tags.map((t) => (
                            <Typography key={t.label} fontSize={9} fontWeight={700}
                              sx={{ bgcolor: t.bg, color: t.color, borderRadius: 0.5, px: 0.4, ml: 0.25 }}>
                              {t.label}
                            </Typography>
                          ))}
                        </Box>
                      );
                    })}
                    {Object.values(role.permissions).filter((p) => p.can_view || p.can_create || p.can_edit || p.can_delete).length > 6 && (
                      <Typography fontSize={11} color="#94a3b8" sx={{ alignSelf: "center" }}>
                        +{Object.values(role.permissions).filter((p) => p.can_view || p.can_create || p.can_edit || p.can_delete).length - 6} more
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Actions */}
                <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                  <Tooltip title="Edit role">
                    <IconButton size="small" onClick={() => openEdit(role)}
                      sx={{ width: 30, height: 30, borderRadius: 1.5, bgcolor: "#eef2ff", color: "#2f3891", "&:hover": { bgcolor: "#e0e7ff" } }}>
                      <EditSharpIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete role">
                    <IconButton size="small" onClick={() => { setDeleteTarget(role); setDeleteDialogOpen(true); }}
                      sx={{ width: 30, height: 30, borderRadius: 1.5, bgcolor: "#fff5f5", color: "#ef4444", "&:hover": { bgcolor: "#fee2e2" } }}>
                      <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      {/* Create / Edit Dialog — Permission Matrix */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
        <Box sx={{ height: 4, bgcolor: "#2f3891" }} />
        <DialogTitle sx={{ pb: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography fontWeight={700} fontSize={15}>{editTarget ? "Edit Role" : "Create New Role"}</Typography>
          <IconButton size="small" onClick={() => setDialogOpen(false)}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: "12px !important" }}>
          <Box sx={{ display: "flex", gap: 2, mb: 2.5 }}>
            <TextField label="Role Name *" size="small" sx={{ flex: 1 }} value={formName}
              onChange={(e) => setFormName(e.target.value)} />
            <TextField label="Description" size="small" sx={{ flex: 2 }} value={formDesc}
              onChange={(e) => setFormDesc(e.target.value)} />
          </Box>

          {/* Permission Matrix */}
          <Typography fontWeight={700} fontSize={13} color="#0f172a" mb={1}>Permission Matrix</Typography>
          <TableContainer component={Paper} elevation={0}
            sx={{ border: "1px solid #e2e8f0", borderRadius: 2, maxHeight: 420, overflow: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: "#374151", bgcolor: "#f8fafc", minWidth: 160 }}>Module</TableCell>
                  {ACTIONS.map((a) => (
                    <TableCell key={a.key} align="center"
                      sx={{ fontWeight: 700, color: a.color, bgcolor: "#f8fafc", minWidth: 80, cursor: "pointer" }}
                      onClick={() => toggleAllForAction(a.key)}>
                      {a.label}
                    </TableCell>
                  ))}
                  <TableCell align="center" sx={{ fontWeight: 700, color: "#64748b", bgcolor: "#f8fafc", minWidth: 60 }}>All</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modules.map((mod) => {
                  const p = formPerms[mod.key] ?? { can_view: false, can_create: false, can_edit: false, can_delete: false };
                  const allOn = ACTIONS.every((a) => p[a.key]);
                  return (
                    <TableRow key={mod.key} hover>
                      <TableCell>
                        <Typography fontSize={13} fontWeight={500} color="#1e293b">{mod.display}</Typography>
                        <Typography fontSize={10} color="#94a3b8" sx={{ fontFamily: "monospace" }}>{mod.key}</Typography>
                      </TableCell>
                      {ACTIONS.map((a) => (
                        <TableCell key={a.key} align="center">
                          <Checkbox size="small" checked={!!p[a.key]}
                            onChange={() => togglePerm(mod.key, a.key)}
                            sx={{ p: 0.25, color: "#cbd5e1", "&.Mui-checked": { color: a.color } }} />
                        </TableCell>
                      ))}
                      <TableCell align="center">
                        <Checkbox size="small" checked={allOn}
                          indeterminate={!allOn && ACTIONS.some((a) => p[a.key])}
                          onChange={() => toggleAllForModule(mod.key)}
                          sx={{ p: 0.25, color: "#cbd5e1", "&.Mui-checked": { color: "#2f3891" } }} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography fontSize={11} color="#94a3b8" mt={1}>
            Click column headers to toggle all modules for that action. Click "All" checkbox to toggle all actions for a module.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button variant="outlined" onClick={() => setDialogOpen(false)}
            sx={{ textTransform: "none", borderRadius: 1.5, borderColor: "#e2e8f0", color: "#64748b" }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={creating || updating}
            sx={{ textTransform: "none", borderRadius: 1.5, bgcolor: "#2f3891", "&:hover": { bgcolor: "#252f78" }, minWidth: 120 }}>
            {(creating || updating) ? <CircularProgress size={18} color="inherit" /> : editTarget ? "Update Role" : "Create Role"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Role?</DialogTitle>
        <DialogContent>
          <Typography fontSize={14} color="text.secondary">
            Delete <strong>{deleteTarget?.name}</strong>? Users with this role will have no role assigned.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting} sx={{ textTransform: "none" }}>
            {deleting ? <CircularProgress size={18} color="inherit" /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserRolesPage;
